/**
 * CustomAssetManager — asset-first animation/audio system.
 *
 * Assets (animation GLBs, face JSONs, audio files) are uploaded to S3 via the
 * avatar-asset-manager service and registered in the DB as independent
 * reusable records. Gestures and prefabs are DB records that reference asset
 * IDs — no files are bundled into them.
 *
 * All asset management calls go to avatar-asset-manager via assetManagerApi.
 */

import { postFileWithProgress } from './upload.js'
import * as aam from './assetManagerApi.js'
import { resolvePreservedAssets, reconcileDerivedMapping } from './assetPreserve.js'
import { planClipRegistration } from './clipRuntimeKeys.js'
import { getBodyClipLoader } from './bodyClipLoaderRef.js'
import { getLiveBodyRig } from './liveBodyRig.js'
import { fetchGlbBytesCached } from './glbCache.js'
import { buildMaterialMappingFromGlb } from './materialMappingFromGlb.js'
import { isRecordGoneError } from './assetErrors.js'
import { toProxiedAssetUrl } from './characterAssetUrl.js'
import { writeMappingCollab, setRemoteMappingHandler } from './collabMappingBridge.js'
import { reconcileRemoteMapping } from './collabMapping.js'
import { uniqueAssetName } from './assetNaming.js'
import { isOrgEnvironmentsEnabled } from './featureFlags.js'
import { reflectPrefabUpsert, reflectPrefabDelete } from './prefabReflection.js'
import { registerBodyClipOnLiveRig } from './registerBodyClipOnRig.js'

// Type-annotated error for version conflicts: wraps the 409 error with a
// clear type hint so callers can discriminate it from other failures.
export class VersionConflictError extends Error {
  constructor(current) {
    super('version_conflict')
    this.name = 'VersionConflictError'
    this.status = 409
    this.current = current
  }
}

let _characterId = 'default'
// The character whose critical data (materials/textures/mapping) last loaded
// cleanly. Used to decide whether a transient fetch error may preserve the
// in-memory data: only safe when re-initing the SAME character, never across a
// switch (that would bleed one character's materials into another).
let _loadedCharacterId = null
// The character an init() is currently fetching for (cleared when it settles).
// Lets ensureInit() dedupe concurrent callers — App.jsx and ContentBrowser both
// want the manager initialised, but only one fetch should run.
let _initInFlightFor = null
let _animationAssets = []
let _audioAssets = []
let _gestures = []
let _prefabs = []
let _blueprintAssets = []
let _materials = []
let _textureAssets = []
let _environmentAssets = []
let _ogsAssets = []
let _materialMapping = []
let _listeners = []

function notify() {
  for (const fn of _listeners) fn()
}

// Derive a material_mapping from the character's rig GLB and persist it.
// The asset-manager doesn't extract material slots on rig upload, so a
// freshly-imported character has an empty mapping; this fetches the rig
// (rig_path is an absolute S3 URL), parses it client-side, PUTs the slot list,
// and returns it. Best-effort: returns [] on any failure (caller keeps the
// empty mapping — loud-logged, never faked).
async function deriveMappingFromRig(characterId) {
  try {
    // Ask the character where its rig is; never compute the path. The rig used
    // to live at ONE key per character, so `/api/character-assets/<id>/rig.glb`
    // happened to be right — and then rig versioning moved it to
    // `rigs/{version}.glb` and a computed path would 404 on every character
    // that had ever been replaced, silently deriving an EMPTY mapping. That is
    // the failure mode CLAUDE.md's "the client never builds, parses, or derives
    // an asset URL" rule exists for (ENG-999). toProxiedAssetUrl only rewrites
    // the host: the raw S3 URL on the row 403s a direct browser fetch, and the
    // credentialed API proxy serves the same object.
    const character = await aam.getCharacter(characterId)
    const rigPath = character?.rigPath
    if (!rigPath) throw new Error('character has no rigPath')
    const res = await fetch(toProxiedAssetUrl(rigPath))
    if (!res.ok) throw new Error(`fetch rig ${res.status}`)
    const derived = buildMaterialMappingFromGlb(await res.arrayBuffer())
    if (derived.length === 0) return []
    // NEVER clobber a server mapping that already exists — re-confirm against
    // authoritative state immediately before the destructive PUT. derive runs
    // off a possibly-stale "empty" init read; persisting the assignment-less
    // derived list here is what wiped users' slot→material bindings (white
    // material after sync). If we can't confirm, use derived for display only.
    let existing = null
    try {
      existing = await aam.getMaterialMapping(characterId)
    } catch (e) {
      console.warn(`[CustomAssetManager] could not confirm server mapping for "${characterId}" — using derived for display, NOT persisting:`, e?.message ?? e)
      return derived
    }
    const { persist, mapping } = reconcileDerivedMapping(derived, existing)
    if (persist) {
      aam.setMaterialMapping(characterId, derived).catch(e =>
        console.warn(`[CustomAssetManager] persist derived material_mapping for "${characterId}" failed:`, e?.message ?? e))
      console.log(`[CustomAssetManager] auto-derived ${derived.length} material slots from rig for "${characterId}"`)
    } else {
      console.warn(`[CustomAssetManager] derived mapping NOT persisted for "${characterId}" — server already has a mapping (preserving its assignments)`)
    }
    return mapping
  } catch (e) {
    console.warn(`[CustomAssetManager] material-mapping auto-derive for "${characterId}" failed:`, e?.message ?? e)
    return []
  }
}

// ── Runtime registration ──────────────────────────────────────────────────────

async function loadBodyClip(name, url, additive = false, aliases = [], version = null) {
  // The seam is AvatarModel's, so it exists for a GLB avatar only. A splat character's
  // body is the part rig, which registerBodyBuffer reaches instead — checked here as
  // well, or a just-uploaded clip returns early and appears only after a reload.
  if (!window.__REGISTER_CUSTOM_BODY_CLIP__ && !getLiveBodyRig()?.register) {
    console.warn('[CustomAssetManager] no body rig to register on '
      + '(neither __REGISTER_CUSTOM_BODY_CLIP__ nor a live body rig)')
    return
  }
  try {
    // Cache-first when the caller knows the row's version token. This is the
    // SAME animCache (URL + updatedAt) AvatarModel's loader fills, so the two no
    // longer each pay for their own copy of the same GLB — a bare fetch() here
    // was an uncached duplicate download of a file already on disk. No token
    // (a blob: preview URL, a row with no updatedAt) → plain fetch, as before.
    if (version != null && !url.startsWith('blob:')) {
      const buf = await fetchGlbBytesCached(url, version)
      await registerBodyBuffer(name, buf, additive, aliases)
      return
    }
    const res = await fetch(url)
    if (!res.ok) {
      // Loud failure beats silent fakery (CLAUDE.md UX rule): when the asset
      // URL returns an error, do NOT feed the error body into GLTFLoader.parse
      // — that produced a misleading "additive A-pose" symptom because the
      // clip was never actually registered and __DIRECT_PLAY__ then stopped
      // the SM idle, snapping the rig to bind pose.
      const preview = (await res.text().catch(() => '')).slice(0, 200)
      throw new Error(`fetch ${res.status} ${res.statusText} for "${name}" at ${url}\n  body preview: ${preview}`)
    }
    await registerBodyBuffer(name, await res.arrayBuffer(), additive, aliases)
  } catch (e) {
    console.error(`[CustomAssetManager] failed to load body clip "${name}":`, e)
    throw e  // let the caller (uploadAnimationAsset / register-runtime) surface this
  }
}

// Register one GLB buffer under a primary key plus any aliases.
//
// The aliases go through __ALIAS_BODY_CLIP__ (clone the registered clip), NOT a
// second __REGISTER_CUSTOM_BODY_CLIP__ call: that seam re-runs GLTFLoader.parse
// and the ~1026-track prune on the SAME bytes, so producing one alias cost as
// much main-thread time as the clip itself. Falls back to a re-parse only if the
// alias seam is absent (an AvatarModel too old to expose it), so an alias is
// never silently dropped.
async function registerBodyBuffer(name, buf, additive, aliases = []) {
  // Splat character: no AvatarModel, so the part rig takes the clip — name and aliases
  // together, since the rig has no alias seam to clone through.
  if (!window.__REGISTER_CUSTOM_BODY_CLIP__) {
    if (await registerBodyClipOnLiveRig(name, buf, aliases)) return
    throw new Error(`registerBodyBuffer: no body rig to register "${name}" on`)
  }
  await window.__REGISTER_CUSTOM_BODY_CLIP__(name, buf, { additive })
  for (const alias of aliases) {
    if (!alias || alias === name) continue
    if (window.__ALIAS_BODY_CLIP__?.(alias, name)) continue
    await window.__REGISTER_CUSTOM_BODY_CLIP__(alias, buf, { additive })
  }
}

// Give one animation asset any runtime key it is STILL missing after its owner
// has had its turn. The ownership rule lives in planClipRegistration
// (clipRuntimeKeys.js, unit-tested); this only executes it.
//
// Body clips are owned by AvatarModel's BodyClipLoader, face clips by
// FaceClipPlayer.loadAll, and both now register an asset's name AND its
// `custom_<kind>_<id>` alias as the clip lands. So the common case here is
// 'skip' — there is nothing left to do, and in particular nothing to download.
//
// It used to take the 'load' path for every row, because it assumed the owner
// had already finished. That held when readiness meant "all clips loaded"; it
// stopped being true when readiness became "rig + PRIORITY clips" with the other
// ~52 on a background sweep. It then raced that sweep from a standing start:
// measured on myra, 58 uncached GLB downloads and 116 parse+prune passes on the
// main thread, during a character switch.
//
// It must not FORCE loading either. An alias is never urgent, and `ensure()`
// deliberately bypasses the loader's pause() — so demanding all 58 up front
// turned the paced background sweep into a burst and defeated the very pacing
// that keeps the main thread responsive. A clip the owner has queued is
// therefore 'skip': the owner will alias it when it gets there.
function registerAnimationAssetKeys(asset, registered) {
  const isFace = asset.kind === 'face'
  const owner = isFace ? window.__FACE_CLIP_PLAYER__ : getBodyClipLoader()
  // "Owned" = the owner has it, or has it queued / in flight. For the body loader
  // that is isManaged; FaceClipPlayer.loadAll is not incremental, so for face it
  // is simply "already loaded".
  const owned = !!asset.name && (isFace
    ? !!owner?.hasClip?.(asset.name)
    : !!owner?.isManaged?.(asset.name))
  const plan = planClipRegistration(asset, { registered, owned })
  if (plan.action === 'skip') return null
  if (plan.action === 'alias') {
    // The name is registered but an alias is not — cheap, synchronous, no I/O.
    if (isFace) {
      for (const alias of plan.aliases) window.__FACE_CLIP_PLAYER__?.aliasClip?.(alias, plan.source)
    } else {
      for (const alias of plan.aliases) window.__ALIAS_BODY_CLIP__?.(alias, plan.source)
    }
    return null
  }
  // Unowned: no loader running (a character with no rig mounted), or a row the
  // owner never got. The only outcome that may touch the network.
  return isFace
    ? loadFaceClip(plan.primary, asset.file_url, plan.aliases)
    : loadBodyClip(plan.primary, asset.file_url, !!asset.additive, plan.aliases, clipVersionOf(asset))
}

// animCache token for a clip URL — changes when the clip is re-uploaded. Same
// field precedence AvatarModel's own loader uses, so both hit the SAME cache
// entry instead of each paying for its own download.
function clipVersionOf(asset) {
  return asset?.updatedAt ?? asset?.updated_at ?? asset?.id ?? null
}

async function loadFaceClip(name, url, aliases = []) {
  if (!window.__FACE_CLIP_PLAYER__) {
    console.warn('[CustomAssetManager] __FACE_CLIP_PLAYER__ not ready')
    return
  }
  try {
    const data = await fetch(url).then(r => r.json())
    window.__FACE_CLIP_PLAYER__.loadCustomClip(name, data)
    // Register under any aliases off the SAME fetched JSON (no refetch) so a
    // clip resolves by both its human name and its custom_face_<id> key.
    for (const alias of aliases) {
      if (alias && alias !== name) window.__FACE_CLIP_PLAYER__.loadCustomClip(alias, data)
    }
  } catch (e) {
    console.warn(`[CustomAssetManager] failed to load face clip "${name}":`, e)
  }
}

async function loadGestureIntoRuntime(gesture) {
  // Stock and custom animation_assets are now both registered at startup —
  // AvatarModel + FaceClipPlayer.loadAll fetch every row and load each clip
  // under its asset.name. FaceClipPlayer.loadAll also builds the gesture
  // map from /api/characters/{slug}/gestures.
  //
  // We used to also register `custom_body_${id}` / `custom_face_${id}`
  // aliases AND overwrite the gesture map from here, which clobbered the
  // canonical name-based mapping. Now this is a no-op for stock gestures.
  // Custom uploads (via the wizard) still register their own aliases in
  // uploadAnimationAsset directly — those don't need to round-trip
  // through here either.
  // Kept the function as a stub so registerToRuntime's call site doesn't
  // need to change.
  return
}

// Resolve the runtime clip name a prefab plays through. Falls back from
// custom-asset URL (downloaded into __AVATAR_ACTIONS__ at `custom_body_{id}`)
// to the stock-clip-name passthrough (already loaded by AvatarModel at
// startup from the static manifest). Returns null when neither is set.
//
// Exported so EventRuntime.playPrefab can use the same resolution.
export function resolvePrefabClipNames(prefab) {
  if (!prefab) return { bodyClipName: null, faceClipName: null }
  const bodyClipName = prefab.body_glb_url
    ? `custom_body_${prefab.id}`
    : (prefab.body_clip_name ?? null)
  const faceClipName = prefab.face_json_url
    ? `custom_face_${prefab.id}`
    : (prefab.face_clip_name ?? null)
  return { bodyClipName, faceClipName }
}

async function loadPrefabIntoRuntime(prefab) {
  // Only download body/face clip files when the prefab references a custom
  // asset (body_glb_url / face_json_url). Stock clips are pre-loaded by
  // AvatarModel from /assets/animations/{body,face}/ — nothing to download,
  // EventRuntime.playPrefab will look them up by name directly.
  const { id, body_glb_url, face_json_url } = prefab
  const tasks = []
  if (body_glb_url)  tasks.push(loadBodyClip(`custom_body_${id}`, body_glb_url, false))
  if (face_json_url) tasks.push(loadFaceClip(`custom_face_${id}`, face_json_url))
  await Promise.all(tasks)
}

// runPrefabReflection — the ONE reflection call per prefab save (two racing
// calls could interleave read-modify-write on the convorcher row and clobber
// each other's phrases). Failure policy: the AAM save is never rolled back.
// When the save carried authored trigger fields (ENG-843) the reflection is
// awaited and its failure rethrown — tagged reflectionOnly so the editor can
// tell "prefab saved, trigger sync failed" apart from a failed save — because
// silently dropping authored phrases is the exact bug the Triggering UI
// exists to kill. Playback-only saves keep the fire-and-forget behavior.
async function runPrefabReflection({ tag, text, phrases, notFor, row }) {
  const reflection = reflectPrefabUpsert({ slug: _characterId, tag, text, phrases, notFor })
  if (phrases === undefined && notFor === undefined) {
    reflection.catch(err => console.error(`[CustomAssetManager] convorcher reflection failed for "${tag}":`, err))
    return
  }
  try {
    await reflection
  } catch (err) {
    const e = new Error(`Prefab saved, but the trigger sync to convorcher failed: ${err?.message ?? err}`)
    e.reflectionOnly = true
    e.row = row
    throw e
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export const CustomAssetManager = {

  // The active character slug (set by init). The prefab Triggering UI uses it
  // to resolve the matching convorcher row — (characterName, name) key.
  getCharacterId() { return _characterId },

  // Initialise the asset manager for the given character. This MUST be
  // called as soon as the active character is known — uploads and list
  // calls use `_characterId` so an init delay means writes get routed to
  // the wrong character. Don't gate on avatar readiness; runtime
  // registration (which DOES need the avatar) is split out into
  // registerToRuntime() and called separately.
  async init(characterId = 'default') {
    // Snapshot the critical material data BEFORE the reset. If a fetch errors
    // (transient AAM 5xx) while re-initing the SAME character, we restore these
    // instead of silently wiping to empty — a server hiccup must never make a
    // character's materials vanish with no signal (CLAUDE.md: no silent
    // fallback to fake/empty data). Only safe within the same character.
    const sameChar = _loadedCharacterId === characterId
    const prevMaterials = _materials
    const prevTextures  = _textureAssets
    const prevMapping   = _materialMapping

    _characterId = characterId
    _initInFlightFor = characterId
    // Reset arrays so a stale previous character's data can't bleed
    // into the new character's listings between init start and finish.
    _animationAssets = []; _audioAssets = []; _gestures = []; _prefabs = []
    _blueprintAssets = []
    _materials = []; _textureAssets = []; _environmentAssets = []; _materialMapping = []
    _ogsAssets = []
    notify()  // let subscribers clear their UI to a loading state

    // Set by a critical-fetch rejection. Surfaced loudly (never silent empty).
    let materialLoadError = null
    const critical = (e) => { materialLoadError = e; return null }

    try {
      const [anims, audio, gestures, prefabs, materials, textures, envs, mapping, ogs, blueprintAssets] = await Promise.all([
        aam.listAnimationAssets(_characterId).catch(() => []),
        aam.listAudioAssets(_characterId).catch(() => []),
        aam.listGestures(_characterId).catch(() => []),
        aam.listPrefabs(_characterId).catch(() => []),
        // Critical: distinguish a fetch ERROR (→ null, preserve prior) from a
        // successful empty list (→ [], a real "no materials" state).
        aam.listMaterials(_characterId).catch(critical),
        aam.listTextureAssets(_characterId).catch(critical),
        isOrgEnvironmentsEnabled() ? aam.listOrgEnvironments().catch(() => []) : Promise.resolve([]),
        aam.getMaterialMapping(_characterId).catch(critical),
        aam.listOgsAssets(_characterId).catch(() => []),
        // Non-critical: an older asset-manager has no /blueprint-assets route, and
        // the character still loads from the singleton blueprint endpoint. An
        // empty library is a real state (nothing saved yet), not a fake one.
        aam.listBlueprintAssets(_characterId).catch(() => []),
      ])
      _animationAssets   = anims
      _audioAssets       = audio
      _gestures          = gestures
      _prefabs           = prefabs
      _blueprintAssets   = blueprintAssets
      _environmentAssets = envs
      _ogsAssets         = ogs
      // On a critical-fetch error (null), keep the prior data for the SAME
      // character; on a real switch fall back to empty (never show another
      // character's materials). A successful [] means the server says empty.
      _materials       = resolvePreservedAssets(materials, sameChar, prevMaterials)
      _textureAssets   = resolvePreservedAssets(textures,  sameChar, prevTextures)
      _materialMapping = resolvePreservedAssets(mapping,   sameChar, prevMapping) ?? []
      // The asset-manager stores an uploaded rig but doesn't parse it, so an
      // imported character has an empty material_mapping → no material slots in
      // the Rig inspector. Derive one slot per mesh from the rig GLB (keyed on
      // node name, exactly what applyMaterialMapping binds against), persist it,
      // and use it — so slots appear on this same load. One-time: once stored,
      // the fetched mapping is non-empty and this is skipped. Skip when the
      // fetch errored — deriving over a transient failure would be misleading. A
      // rigless splat character 404s the rig fetch and derive returns [] harmlessly.
      if (_materialMapping.length === 0 && !materialLoadError) {
        _materialMapping = await deriveMappingFromRig(_characterId)
      }
      if (!materialLoadError) _loadedCharacterId = characterId
    } catch (e) {
      // Non-critical lists failed hard; preserve critical material data for the
      // same character rather than wiping it.
      materialLoadError = e
      _animationAssets = []; _audioAssets = []; _gestures = []; _prefabs = []
      _blueprintAssets = []
      _environmentAssets = []; _ogsAssets = []
      _materials       = sameChar ? prevMaterials : []
      _textureAssets   = sameChar ? prevTextures  : []
      _materialMapping = sameChar ? prevMapping   : []
    }

    if (_initInFlightFor === characterId) _initInFlightFor = null

    if (materialLoadError) {
      // Loud failure beats silent fakery — log + expose so the UI can surface it.
      console.error('[CustomAssetManager] material load failed for',
        characterId, '— preserved prior data (no silent wipe):', materialLoadError)
    }
    if (typeof window !== 'undefined') {
      window.__ASSET_LOAD_ERROR__ = materialLoadError
        ? String(materialLoadError?.message ?? materialLoadError) : null
      window.__ENVIRONMENTS_LIST__ = _environmentAssets
      window.__OGS_LIST__ = _ogsAssets
    }

    notify()
    console.log(`[CustomAssetManager] ${_animationAssets.length} anim, ${_audioAssets.length} audio, ${_gestures.length} gestures, ${_prefabs.length} prefabs, ${_materials.length} materials, ${_textureAssets.length} textures, ${_environmentAssets.length} envs`)

    // Best-effort runtime registration. Skips silently when the avatar
    // isn't loaded yet — the caller (App) will invoke registerToRuntime()
    // again from handleAvatarLoaded.
    this.registerToRuntime()
  },

  // Initialise for `characterId` only if it isn't already loaded or loading.
  // Multiple consumers want the manager ready: App.jsx (docked) AND
  // ContentBrowser. In the undocked editor popup App.jsx is NOT mounted, so
  // ContentBrowser must drive init itself — but in the docked window both call
  // this and only one fetch should run. Returns the in-flight init() promise so
  // callers can await readiness; resolves immediately when already loaded.
  ensureInit(characterId = 'default') {
    if (_loadedCharacterId === characterId || _initInFlightFor === characterId) {
      return Promise.resolve()
    }
    return this.init(characterId)
  },

  // Re-fetch every asset list from the server WITHOUT a full page reload, then
  // register any newly-discovered animation clips on the live mixer. Use this
  // when assets were added out-of-band (e.g. synced to the cloud from another
  // tool) — the running session otherwise only learns about them at init()
  // (page load), which is why a freshly-synced clip "needs a refresh to appear
  // / start looping". Unlike init(), this does NOT clear the in-memory lists
  // first, so the UI never flashes empty mid-refresh.
  async refresh() {
    if (!_characterId) return
    try {
      const [anims, audio, gestures, prefabs, materials, textures, envs, mapping, ogs, blueprintAssets] = await Promise.all([
        aam.listAnimationAssets(_characterId).catch(() => null),
        aam.listAudioAssets(_characterId).catch(() => null),
        aam.listGestures(_characterId).catch(() => null),
        aam.listPrefabs(_characterId).catch(() => null),
        aam.listMaterials(_characterId).catch(() => null),
        aam.listTextureAssets(_characterId).catch(() => null),
        isOrgEnvironmentsEnabled() ? aam.listOrgEnvironments().catch(() => null) : Promise.resolve(null),
        aam.getMaterialMapping(_characterId).catch(() => null),
        aam.listOgsAssets(_characterId).catch(() => null),
        // The blueprint library belongs in the refresh for the same reason
        // every other list does: a blueprint saved from another tool (or
        // another tab) is exactly the out-of-band change this exists for.
        aam.listBlueprintAssets(_characterId).catch(() => null),
      ])
      if (anims)     _animationAssets   = anims
      if (audio)     _audioAssets       = audio
      if (gestures)  _gestures          = gestures
      if (prefabs)   _prefabs           = prefabs
      if (materials) _materials         = materials
      if (textures)  _textureAssets     = textures
      if (envs)      _environmentAssets = envs
      if (ogs)       _ogsAssets         = ogs
      if (mapping)   _materialMapping   = mapping
      if (blueprintAssets) _blueprintAssets = blueprintAssets
    } catch (e) {
      console.warn('[CustomAssetManager] refresh fetch failed:', e)
      throw e
    }

    if (typeof window !== 'undefined') {
      window.__ENVIRONMENTS_LIST__ = _environmentAssets
      window.__OGS_LIST__ = _ogsAssets
    }

    // Register clips the runtime hasn't seen yet — by NAME, so a name-selected
    // Play Clip node resolves to a real mixer action (and can therefore loop).
    // AvatarModel only registers by name at startup; without this a freshly-
    // synced clip stays unbound until a full page reload.
    if (typeof window !== 'undefined' && window.__REGISTER_CUSTOM_BODY_CLIP__) {
      const existingBody = window.__AVATAR_ACTIONS__ ?? {}
      const faceClips = window.__FACE_CLIP_PLAYER__?._clips ?? {}
      await Promise.all(
        _animationAssets.map(a => {
          if (a.kind === 'body' && !existingBody[a.name]) return loadBodyClip(a.name, a.file_url, !!a.additive, [], clipVersionOf(a))
          if (a.kind === 'face' && !faceClips[a.name])     return loadFaceClip(a.name, a.file_url)
          return null
        }).filter(Boolean),
      ).catch(e => console.warn('[CustomAssetManager] refresh clip registration failed:', e))
    }

    notify()
    console.log(`[CustomAssetManager] refreshed: ${_animationAssets.length} anim, ${_audioAssets.length} audio, ${_gestures.length} gestures, ${_prefabs.length} prefabs`)
  },

  // Register the currently-loaded gestures, prefabs, AND raw animation/face
  // assets into the runtime (__AVATAR_ACTIONS__, __FACE_CLIP_PLAYER__). Safe
  // to call multiple times — idempotent. Returns immediately if the avatar
  // globals aren't ready.
  //
  // Raw assets are registered at `custom_body_<asset_id>` / `custom_face_<asset_id>`
  // — the same key the Clips-panel Test button resolves to. Without this, an
  // uploaded clip works in the session it was uploaded in (uploadAnimationAsset
  // registers it inline) but goes MISSING on the next page reload, surfacing
  // as A-pose when the Test button stops idle for a clip that isn't there.
  registerToRuntime() {
    if (typeof window === 'undefined') return
    if (!window.__FACE_CLIP_PLAYER__?._loaded || !window.__REGISTER_CUSTOM_BODY_CLIP__) {
      console.log('[CustomAssetManager] runtime not ready, deferring registration')
      return
    }
    // Live key→action maps AvatarModel already populated by NAME (JIT loader).
    // We register only the keys these are MISSING — re-registering a present
    // key force-REPLACES the live action (orphaning held references, the
    // prefab-blend-envelope CI-hang root cause) and re-downloads uncached.
    const bodyActions = window.__AVATAR_ACTIONS__ ?? {}
    const faceClips   = window.__FACE_CLIP_PLAYER__?._clips ?? {}
    Promise.all([
      ..._animationAssets.map(a => registerAnimationAssetKeys(a, a.kind === 'face' ? faceClips : bodyActions)).filter(Boolean),
      ..._gestures.map(g => loadGestureIntoRuntime(g)),
      ..._prefabs.filter(p => p.status === 'ready').map(p => loadPrefabIntoRuntime(p)),
    ]).catch(e => console.warn('[CustomAssetManager] runtime registration failed:', e))
  },

  listAnimationAssets() { return _animationAssets },
  listAudioAssets()     { return _audioAssets },
  listGestures()        { return _gestures },
  listPrefabs()         { return _prefabs },
  listBlueprintAssets() { return _blueprintAssets },
  listMaterials()       { return _materials },
  listTextureAssets()   { return _textureAssets },
  listEnvironmentAssets() { return _environmentAssets },
  listOgsAssets() { return _ogsAssets },
  listMaterialMapping() { return _materialMapping },
  resolvePrefabClipNames,

  // Ensure a prefab's custom body/face clips are downloaded and registered in
  // the runtime. Idempotent — skips the fetch when the clips are already
  // present. Prefab clips load asynchronously on character init / after
  // create, so the Test path awaits this to avoid firing playback at a
  // not-yet-registered clip (which flashes the avatar back to rest pose).
  async ensurePrefabLoaded(prefab) {
    if (!prefab) return
    const bodyKey = prefab.body_glb_url ? `custom_body_${prefab.id}` : null
    const faceKey = prefab.face_json_url ? `custom_face_${prefab.id}` : null
    const actions = (typeof window !== 'undefined' && window.__AVATAR_ACTIONS__) || {}
    const facePlayer = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
    const bodyReady = !bodyKey || (bodyKey in actions)
    const faceReady = !faceKey || !!facePlayer?._clips?.[faceKey]
    if (bodyReady && faceReady) return
    await loadPrefabIntoRuntime(prefab)
  },

  // ENG-921: the convorcher-backed GSS hair path (gss_hair config + gss_hair_assets
  // rows + the /api/gss-hair-* file proxies) was removed here. ENG-579 deleted the
  // handlers and migration 00028 dropped the column and the table; AAM has no
  // equivalent, so every one of those calls 404'd. The two LIVE splat-hair paths
  // are untouched: OGSModel mounts hair.gsh from the character's AAM /ogs bundle,
  // and `?hairlocal=1` renders public/assets/hair_static/ with no backend.

  subscribe(fn) {
    _listeners.push(fn)
    return () => { _listeners = _listeners.filter(l => l !== fn) }
  },

  // ── Animation asset upload ──────────────────────────────────────────────────

  async uploadAnimationAsset(file, { name, kind, category = 'other', additive = false, loopable = false, durationMs } = {}) {
    const fd = new FormData()
    fd.append('file', file, file.name)
    fd.append('name', name)
    fd.append('kind', kind)
    fd.append('category', category)
    fd.append('additive', String(additive))
    fd.append('loopable', String(loopable))
    if (durationMs != null) fd.append('durationMs', String(durationMs))

    const row = await aam.uploadAnimationAsset(_characterId, fd)

    if (kind === 'body') {
      // Register under the asset NAME so name-based lookups (gesture map,
      // PlayClipNode dropdown, blueprint references) work uniformly across
      // stock + custom uploads. Also register the legacy custom_body_<id>
      // alias for any code that still resolves by ID.
      // ONE fetch, both keys: the alias is produced from the registered clip
      // (see registerBodyBuffer), not by downloading and re-parsing the GLB a
      // second time as this used to.
      await loadBodyClip(row.name, row.file_url, additive, [`custom_body_${row.id}`])
    } else if (kind === 'face') {
      await loadFaceClip(row.name, row.file_url, [`custom_face_${row.id}`])
    }

    _animationAssets = [..._animationAssets, row]
    notify()
    return row
  },

  // ENG-661/ENG-676: upload a source FBX and convert it server-side. kind="body"
  // (default) → body GLB (Blender); kind="face" → ARKit face JSON (python).
  // Returns the row in conversion_status="converting"; call pollConversion(row.id)
  // to await the terminal state.
  async convertAnimationAsset(file, { name, kind = 'body', category = 'other', additive = false, loopable = false } = {}) {
    const fd = new FormData()
    fd.append('file', file, file.name)
    fd.append('name', name)
    fd.append('kind', kind)
    fd.append('category', category)
    fd.append('additive', String(additive))
    fd.append('loopable', String(loopable))

    const row = await aam.convertAnimationAsset(_characterId, fd)
    _animationAssets = [..._animationAssets, row]
    notify()
    return row
  },

  // Poll the server until a converting row reaches a terminal state. Resolves
  // with the final row ("ready" or "error"). On success the clip is registered
  // at runtime by name + custom_{body,face}_<id> alias, picked by the row's kind.
  async pollConversion(id, { intervalMs = 1500, timeoutMs = 180000 } = {}) {
    const deadline = Date.now() + timeoutMs
    for (;;) {
      const list = await aam.listAnimationAssets(_characterId)
      const row = list.find(a => a.id === id)
      if (row) {
        _animationAssets = _animationAssets.map(a => (a.id === id ? row : a))
        notify()
        const status = row.conversion_status ?? row.conversionStatus
        if (status === 'ready') {
          if ((row.kind ?? row.type) === 'face') {
            await loadFaceClip(row.name, row.file_url, [`custom_face_${row.id}`])
          } else {
            await loadBodyClip(row.name, row.file_url, row.additive, [`custom_body_${row.id}`])
          }
          return row
        }
        if (status === 'error') return row
      }
      if (Date.now() > deadline) {
        return row ?? { id, conversion_status: 'error', conversion_error: 'timed out waiting for conversion' }
      }
      await new Promise(r => setTimeout(r, intervalMs))
    }
  },

  async deleteAnimationAsset(id) {
    await aam.deleteAnimationAsset(_characterId, id)
    _animationAssets = _animationAssets.filter(a => a.id !== id)
    notify()
  },

  async updateAnimationAsset(id, patch) {
    const row = _animationAssets.find(a => a.id === id)
    const version = row?.version
    try {
      const updated = await aam.updateAnimationAsset(id, patch, { version })
      _animationAssets = _animationAssets.map(a => a.id === id ? updated : a)
      notify()
      return updated
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        _animationAssets = _animationAssets.map(a => a.id === id ? err.current : a)
        notify()
        throw new VersionConflictError(err.current)
      }
      throw err
    }
  },

  // ── Audio asset upload ──────────────────────────────────────────────────────

  async uploadAudioAsset(file, { name, transcript, durationMs } = {}) {
    const fd = new FormData()
    fd.append('file', file, file.name)
    fd.append('name', name)
    if (transcript) fd.append('transcript', transcript)
    if (durationMs != null) fd.append('durationMs', String(durationMs))

    const row = await aam.uploadAudioAsset(_characterId, fd)
    _audioAssets = [..._audioAssets, row]
    notify()
    return row
  },

  async deleteAudioAsset(id) {
    await aam.deleteAudioAsset(_characterId, id)
    _audioAssets = _audioAssets.filter(a => a.id !== id)
    notify()
  },

  async updateAudioAsset(id, patch) {
    const row = _audioAssets.find(a => a.id === id)
    const version = row?.version
    try {
      const updated = await aam.updateAudioAsset(id, patch, { version })
      _audioAssets = _audioAssets.map(a => a.id === id ? updated : a)
      notify()
      return updated
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        _audioAssets = _audioAssets.map(a => a.id === id ? err.current : a)
        notify()
        throw new VersionConflictError(err.current)
      }
      throw err
    }
  },

  // ── Environment asset upload ────────────────────────────────────────────────

  async uploadEnvironmentAsset(file, { name, folderPath, defaultPose, onProgress } = {}) {
    const fd = new FormData()
    fd.append('file', file, file.name)
    if (name) fd.append('name', name)
    if (folderPath) fd.append('folder_path', folderPath)
    if (defaultPose) fd.append('default_pose', JSON.stringify(defaultPose))

    // Use XHR when the caller wants progress — fetch() has no upload-progress
    // event. (ReadableStream-as-body would in theory work but Chrome still
    // requires HTTPS/2 and an experimental flag for it.)
    const aamBase = import.meta.env.VITE_ASSET_MANAGER_URL || 'http://localhost:8081'
    const aamKey  = import.meta.env.VITE_ASSET_MANAGER_API_KEY || ''
    // X-API-Key is a localhost-dev affordance — cookie can't reach localhost.
    // Upload to org environments endpoint (no character slug).
    const row = await postFileWithProgress(
      `${aamBase}/api/org/environments`,
      fd,
      { onProgress, headers: aamKey ? { 'X-API-Key': aamKey } : {} },
    )

    _environmentAssets = [..._environmentAssets, row]
    if (typeof window !== 'undefined') window.__ENVIRONMENTS_LIST__ = _environmentAssets
    notify()
    return row
  },

  async deleteEnvironmentAsset(id) {
    // Capture the URL before deleting so we can clear it from liveTuneStore
    // if the active splat was this one — otherwise the splat stays rendered
    // in the scene even though its DB row + S3 object are gone.
    const row = _environmentAssets.find(a => a.id === id)
    const deletedUrl = row ? (row.fileUrl ?? row.file_url ?? null) : null

    try {
      await aam.deleteOrgEnvironment(id)
    } catch (err) {
      // Already gone server-side (stale client) — re-sync so the card drops
      // and treat the delete as effectively done rather than erroring out.
      if (isRecordGoneError(err)) { await this.refresh(); return }
      throw err
    }
    _environmentAssets = _environmentAssets.filter(a => a.id !== id)
    if (typeof window !== 'undefined') window.__ENVIRONMENTS_LIST__ = _environmentAssets

    if (deletedUrl) {
      // Async-import the store to avoid a top-level dep on the UI layer
      // (CustomAssetManager is imported very early during boot).
      try {
        const mod = await import('./liveTuneStore')
        const current = mod.liveTuneStore.getState().environment?.splatUrl
        if (current === deletedUrl) {
          // assetId goes with the URL — see resolveCurrentEnvironment.
          mod.liveTuneStore.setState({ environment: { splatUrl: null, assetId: null } })
        }
      } catch (e) {
        console.warn('[CustomAssetManager] could not clear deleted env from liveTuneStore:', e)
      }
    }

    notify()
  },

  async updateEnvironmentAsset(id, patch) {
    const row = _environmentAssets.find(a => a.id === id)
    const version = row?.version
    try {
      const updated = await aam.updateOrgEnvironment(id, patch, { version })
      _environmentAssets = _environmentAssets.map(a => a.id === id ? updated : a)
      if (typeof window !== 'undefined') window.__ENVIRONMENTS_LIST__ = _environmentAssets
      notify()
      return updated
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        _environmentAssets = _environmentAssets.map(a => a.id === id ? err.current : a)
        if (typeof window !== 'undefined') window.__ENVIRONMENTS_LIST__ = _environmentAssets
        notify()
        throw new VersionConflictError(err.current)
      }
      // Stale client list pointing at a deleted/superseded asset (another
      // session replaced it). Re-sync from the server so the dead card drops,
      // then rethrow so the caller can show a clear "asset is gone" message.
      if (isRecordGoneError(err)) { await this.refresh() }
      throw err
    }
  },

  async bakeEnvironmentAsset(id, params = {}) {
    try {
      const updated = await aam.bakeOrgEnvironment(id, params)
      _environmentAssets = _environmentAssets.map(a => a.id === id ? updated : a)
      if (typeof window !== 'undefined') window.__ENVIRONMENTS_LIST__ = _environmentAssets
      notify()
      return updated
    } catch (err) {
      if (isRecordGoneError(err)) { await this.refresh(); return }
      throw err
    }
  },

  // ── OGS bundle (loose per-character inference files, /characters/{slug}/ogs) ──

  // Upload one OGS bundle file (geom/appr/mesh/rig/manifest …). The collection is
  // keyed by bare filename, so re-uploading a name replaces its row. XHR for
  // progress; loud failure per CLAUDE.md.
  async uploadOgsAsset(file, { onProgress } = {}) {
    const fd = new FormData()
    fd.append('file', file, file.name)
    const aamBase = import.meta.env.VITE_ASSET_MANAGER_URL || 'http://localhost:8081'
    const aamKey  = import.meta.env.VITE_ASSET_MANAGER_API_KEY || ''
    const row = await postFileWithProgress(
      `${aamBase}/api/characters/${_characterId}/ogs`,
      fd,
      { onProgress, headers: aamKey ? { 'X-API-Key': aamKey } : {} },
    )
    const fname = row?.filename ?? row?.name ?? file.name
    _ogsAssets = [..._ogsAssets.filter(a => (a.filename ?? a.name) !== fname), row]
    if (typeof window !== 'undefined') window.__OGS_LIST__ = _ogsAssets
    notify()
    return row
  },

  async deleteOgsAsset(filename) {
    try {
      await aam.deleteOgsAsset(_characterId, filename)
    } catch (err) {
      // Already gone server-side (stale client) — re-sync and treat as done.
      if (isRecordGoneError(err)) { await this.refresh(); return }
      throw err
    }
    _ogsAssets = _ogsAssets.filter(a => (a.filename ?? a.name) !== filename)
    if (typeof window !== 'undefined') window.__OGS_LIST__ = _ogsAssets
    notify()
  },

  // ── Gesture management ──────────────────────────────────────────────────────

  async createGesture({ tag, displayName, bodyAnimationId, faceAnimationId, durationMs = 3000, bodyWeight = 0.7 }) {
    const row = await aam.createGesture(_characterId, { tag, displayName, bodyAnimationId, faceAnimationId, durationMs, bodyWeight })

    // Resolve URLs from local asset cache for runtime registration
    const bodyAsset = bodyAnimationId ? _animationAssets.find(a => a.id === bodyAnimationId) : null
    const faceAsset = faceAnimationId ? _animationAssets.find(a => a.id === faceAnimationId) : null
    await loadGestureIntoRuntime({
      ...row,
      body_glb_url:  bodyAsset?.file_url,
      face_json_url: faceAsset?.file_url,
    })

    _gestures = [..._gestures.filter(g => g.tag !== tag), { ...row, body_glb_url: bodyAsset?.file_url, face_json_url: faceAsset?.file_url }]
    notify()
    return row
  },

  async updateGesture(id, fields) {
    const row = _gestures.find(g => g.id === id)
    const version = row?.version
    try {
      const updated = await aam.updateGesture(id, fields, { version })
      _gestures = _gestures.map(g => g.id === id ? updated : g)
      notify()
      return updated
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        _gestures = _gestures.map(g => g.id === id ? err.current : g)
        notify()
        throw new VersionConflictError(err.current)
      }
      throw err
    }
  },

  async deleteGesture(id) {
    await aam.deleteGesture(id)
    _gestures = _gestures.filter(g => g.id !== id)
    notify()
  },

  // ── Prefab management ───────────────────────────────────────────────────────

  // Prefab create accepts EITHER bodyAnimationId (custom uploaded asset) OR
  // bodyClipName (a stock clip baked into the static manifest) per slot.
  // Same for face. The two are mutually exclusive — only one populates the
  // server-side row. See loadPrefabIntoRuntime for the runtime resolution.
  //
  // phrases / notFor (ENG-843) are NOT AAM columns — they live on the
  // convorcher phrase-trigger row and only flow through the reflection call.
  // string[] = the Triggering UI authored them; undefined = preserve the
  // row's existing values (pre-843 behavior).
  async createPrefab({ tag, displayName, bodyAnimationId, faceAnimationId, bodyClipName, faceClipName, audioAssetId, durationMs, blendInMs = 180, blendOutMs = 220, loop = false, text, status = 'draft', phrases, notFor }) {
    const row = await aam.createPrefab(_characterId, { tag, displayName, bodyAnimationId, faceAnimationId, bodyClipName, faceClipName, audioAssetId, durationMs, blendInMs, blendOutMs, loop, text, status })

    // Re-fetch the full list so the in-memory cache has joined URLs
    // (audio_url, body_glb_url, face_json_url) that the runtime reads.
    _prefabs = await aam.listPrefabs(_characterId).catch(() => [..._prefabs.filter(p => p.tag !== tag), row])
    const stored = _prefabs.find(p => p.id === row.id) ?? row

    if (stored.status === 'ready') {
      await loadPrefabIntoRuntime(stored)
    }

    notify()
    // ENG-834: mirror into convorcher so the prefab can FIRE in conversation
    // (phrase-trigger row + story links). The AAM save already succeeded and
    // is never rolled back; when the author explicitly wrote trigger data the
    // reflection is AWAITED so a sync failure surfaces in the editor instead
    // of only the console (an unreflected prefab never fires).
    await runPrefabReflection({ tag, text, phrases, notFor, row: stored })
    return stored
  },

  async updatePrefab(id, fields) {
    // Trigger fields ride alongside the AAM fields in one save call but are
    // convorcher-only — strip them from the PATCH body (ENG-843).
    const { phrases, notFor, ...aamFields } = fields ?? {}
    const row = _prefabs.find(p => p.id === id)
    const version = row?.version
    let updated
    try {
      updated = await aam.updatePrefab(id, aamFields, { version })
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        _prefabs = _prefabs.map(p => p.id === id ? err.current : p)
        notify()
        throw new VersionConflictError(err.current)
      }
      throw err
    }
    _prefabs = _prefabs.map(p => p.id === id ? updated : p)
    notify()
    // Re-register so a body/face/audio swap takes effect without a reload.
    if (updated && updated.status === 'ready') {
      await loadPrefabIntoRuntime(updated)
    }
    // ENG-834: keep the convorcher phrase-trigger row in sync (alternate
    // text + story links). See createPrefab for the failure policy.
    await runPrefabReflection({
      tag: updated?.tag ?? row?.tag,
      text: updated?.text ?? updated?.responseText,
      phrases,
      notFor,
      row: updated,
    })
    return updated
  },

  async deletePrefab(id) {
    const tag = _prefabs.find(p => p.id === id)?.tag
    await aam.deletePrefab(id)
    _prefabs = _prefabs.filter(p => p.id !== id)
    // ENG-834: delete mirrors delete — remove the convorcher phrase-trigger
    // row so the LLM stops being offered a prefab that no longer exists.
    if (tag) {
      reflectPrefabDelete({ slug: _characterId, tag })
        .catch(err => console.error(`[CustomAssetManager] convorcher delete reflection failed for "${tag}":`, err))
    }
    notify()
  },

  // ── In-memory preview (no upload, no persistence) ───────────────────────────

  async previewGesture(bodyGlbFile, faceJsonFile, durationMs = 3000, bodyWeight = 0.7) {
    const tmpName = `__preview_${Date.now()}`

    if (bodyGlbFile && window.__REGISTER_CUSTOM_BODY_CLIP__) {
      const buf = await bodyGlbFile.arrayBuffer()
      await window.__REGISTER_CUSTOM_BODY_CLIP__(tmpName, buf, { additive: true })
      window.__GESTURE_CHANNEL__?.play(tmpName, { durationMs, peakWeight: bodyWeight })
    }

    if (faceJsonFile && window.__FACE_CLIP_PLAYER__) {
      const data = JSON.parse(await faceJsonFile.text())
      const faceName = `${tmpName}_face`
      window.__FACE_CLIP_PLAYER__.loadCustomClip(faceName, data)
      window.__FACE_CLIP_PLAYER__.manualPlay(faceName, 0.8, durationMs)
    }
  },

  async previewPrefab(bodyGlbFile, faceJsonFile, audioFile, durationMs = 5000, blendInMs = 180, blendOutMs = 220) {
    const tmpName = `__preview_${Date.now()}`

    if (bodyGlbFile && window.__REGISTER_CUSTOM_BODY_CLIP__) {
      const buf = await bodyGlbFile.arrayBuffer()
      await window.__REGISTER_CUSTOM_BODY_CLIP__(tmpName, buf, { additive: false })
      const actualMs = window.__AVATAR_ACTIONS__?.[tmpName]
        ? Math.round(window.__AVATAR_ACTIONS__[tmpName].getClip().duration * 1000)
        : durationMs
      const now = Date.now()
      window.__DIRECT_PLAY__ = {
        clipName: tmpName,
        startTime: now,
        expiresAt: now + actualMs,
        blendInMs,
        blendOutMs,
      }
    }

    if (faceJsonFile && window.__FACE_CLIP_PLAYER__) {
      const data = JSON.parse(await faceJsonFile.text())
      const faceName = `${tmpName}_face`
      window.__FACE_CLIP_PLAYER__.loadCustomClip(faceName, data)
      window.__FACE_CLIP_PLAYER__.manualPlay(faceName, 0.8, durationMs)
    }

    if (audioFile) {
      const url = URL.createObjectURL(audioFile)
      const audio = new Audio(url)
      audio.play().catch(() => {})
      setTimeout(() => URL.revokeObjectURL(url), durationMs + 1000)
    }
  },

  // ── Blueprint assets (the blueprint library, ENG-1030) ─────────────────────
  //
  // Cached rows carry METADATA only — name, folder, isDefault, version. Graph
  // content is fetched per blueprint by getBlueprintAsset, because three graph
  // documents per row would make the list response the largest fetch on boot
  // for a benefit nothing on screen needs.

  async createBlueprintAsset({ name = 'New Graph', kind, folderPath = '/blueprints', graph, setDefault = false } = {}) {
    if (!kind) throw new Error('createBlueprintAsset: kind is required (body | face | events)')
    // Live rows are unique per (character, KIND, name) server-side (migration
    // 00039), so uniquify against the same kind only — a body graph and a face
    // graph may both be called "Main", which is what the migration produced.
    const uniqueName = uniqueAssetName(name, _blueprintAssets.filter(b => b.kind === kind).map(b => b.name))
    const payload = { name: uniqueName, kind, folderPath, setDefault }
    if (graph !== undefined) payload.graph = graph
    const row = await aam.createBlueprintAsset(_characterId, payload)
    // Creating with setDefault demotes whichever row held the flag, so refresh
    // the whole list rather than patching one entry — a stale `isDefault` on a
    // sibling card is exactly the lie this feature must not tell.
    _blueprintAssets = setDefault
      ? await aam.listBlueprintAssets(_characterId).catch(() => [..._blueprintAssets, row])
      : [..._blueprintAssets, row]
    notify()
    return row
  },

  // Full content for one blueprint, including its three graphs. Not cached:
  // the caller either loads it into the editor (which owns it from then on) or
  // copies it, and a cached graph would be a second source of truth for the
  // thing localStorage already caches.
  getBlueprintAssetContent(id) {
    return aam.getBlueprintAsset(id)
  },

  async updateBlueprintAsset(id, patch) {
    const row = _blueprintAssets.find(b => b.id === id)
    const version = row?.version
    try {
      const updated = await aam.updateBlueprintAsset(id, patch, { version })
      _blueprintAssets = _blueprintAssets.map(b => b.id === id ? { ...b, ...updated } : b)
      notify()
      return updated
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        _blueprintAssets = _blueprintAssets.map(b => b.id === id ? err.current : b)
        notify()
        throw new VersionConflictError(err.current)
      }
      throw err
    }
  },

  async deleteBlueprintAsset(id) {
    // The server refuses to delete the default (it is what the character
    // loads) — that rejection propagates so the UI can say so, rather than
    // being swallowed into a card that silently stays put.
    await aam.deleteBlueprintAsset(id)
    _blueprintAssets = _blueprintAssets.filter(b => b.id !== id)
    notify()
  },

  async setDefaultBlueprintAsset(id) {
    const updated = await aam.setDefaultBlueprintAsset(id)
    // One default per character: clear the flag everywhere, then set it here,
    // so two cards can never both claim it while the list is stale.
    // One default per KIND: clear the flag only among rows of the same kind, so
    // promoting a body graph does not un-default the event graph.
    const kind = updated?.kind ?? _blueprintAssets.find(b => b.id === id)?.kind
    _blueprintAssets = _blueprintAssets.map(b =>
      b.id === id ? { ...b, ...updated, isDefault: true }
        : (b.kind === kind ? { ...b, isDefault: false } : b))
    notify()
    return updated
  },

  async restoreBlueprintAsset(id) {
    const restored = await aam.restoreBlueprintAsset(id)
    _blueprintAssets = [..._blueprintAssets.filter(b => b.id !== id), restored]
    notify()
    return restored
  },

  // ── Material assets ────────────────────────────────────────────────────────

  async createMaterial({ name = 'New Material', type = 'pbr', folderPath = '/materials', data } = {}) {
    // Live materials are unique per (character, name) server-side (migration
    // 00024). Creating a second "New Graph Material" otherwise fails with a raw
    // duplicate-key 23505. Uniquify against the current live set so repeated
    // default-name creates just increment (" 2", " 3", …).
    const uniqueName = uniqueAssetName(name, _materials.map(m => m.name))
    const body = { name: uniqueName, type, folder_path: folderPath }
    if (data !== undefined) body.data = data
    const row = await aam.createMaterial(_characterId, body)
    _materials = [..._materials, row]
    notify()
    return row
  },

  async updateMaterial(id, patch) {
    const row = _materials.find(m => m.id === id)
    const version = row?.version
    try {
      const updated = await aam.updateMaterial(id, patch, { version })
      // Update the cached row with the new version from the response
      _materials = _materials.map(m => m.id === id ? { ...updated, data: updated.data ?? m.data } : m)
      notify()
      return updated
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        // Update cache to the server's current state so the UI shows fresh truth
        _materials = _materials.map(m => m.id === id ? err.current : m)
        notify()
        // Throw a typed error so the caller can prompt for reconciliation
        throw new VersionConflictError(err.current)
      }
      throw err
    }
  },

  async deleteMaterial(id) {
    await aam.deleteMaterial(id)
    _materials = _materials.filter(m => m.id !== id)
    notify()
  },

  // ── Texture assets ─────────────────────────────────────────────────────────

  async uploadTextureAsset(file, { name, folderPath = '/textures' } = {}) {
    const fd = new FormData()
    fd.append('file', file, file.name)
    if (name) fd.append('name', name)
    fd.append('folder_path', folderPath)
    const row = await aam.uploadTextureAsset(_characterId, fd)
    _textureAssets = [..._textureAssets, row]
    notify()
    return row
  },

  async updateTextureAsset(id, patch) {
    const row = _textureAssets.find(t => t.id === id)
    const version = row?.version
    try {
      const updated = await aam.updateTextureAsset(id, patch, { version })
      _textureAssets = _textureAssets.map(t => t.id === id ? updated : t)
      notify()
      return updated
    } catch (err) {
      if (aam.isVersionConflict(err)) {
        _textureAssets = _textureAssets.map(t => t.id === id ? err.current : t)
        notify()
        throw new VersionConflictError(err.current)
      }
      throw err
    }
  },

  async deleteTextureAsset(id) {
    await aam.deleteTextureAsset(id)
    _textureAssets = _textureAssets.filter(t => t.id !== id)
    notify()
  },

  // ── Material mapping (mesh-slot → material binding) ────────────────────────

  async putMaterialMapping(mapping) {
    // Co-editing (ENG-539 Stage 2): when a collab session is live for this
    // character, route the edit into the shared Yjs doc — it broadcasts to the
    // other editors and the collab service persists to AAM (server stays the
    // source of truth, no double-write). Solo/offline, write straight to AAM.
    if (writeMappingCollab(mapping)) {
      _materialMapping = mapping
      notify()
      return
    }
    await aam.setMaterialMapping(_characterId, mapping)
    _materialMapping = mapping
    notify()
  },
}

// Apply a mapping change that arrived from another editor over the collab doc:
// update in-memory state, re-render the content browser (registry rebuilds off
// notify), and rebind the avatar using the fresh mapping directly — never a
// stale AAM re-fetch (the collab service persists only debounced).
//
// The shared Y.Array starts EMPTY and its first observe fires [] before it is
// seeded from the server mapping — blindly applying that clobbered the
// server-loaded slots the moment a collab session connected ("Myra material
// slots disappeared"). reconcileRemoteMapping returns null for that
// empty-over-populated case so we leave the loaded mapping intact.
setRemoteMappingHandler((rows) => {
  const applied = reconcileRemoteMapping(_materialMapping, rows)
  if (applied === null) return
  _materialMapping = applied
  notify()
  if (typeof window !== 'undefined') window.__RELOAD_MATERIALS__?.({ mapping: applied })
})

// Test/debug handle (ENG-834): lets Playwright observe the loaded asset lists
// (e.g. wait for listPrefabs() to be non-empty before simulating a Convorcher
// NewResponse). Live singleton reference — same pattern as __EVENT_RUNTIME__.
if (typeof window !== 'undefined') {
  window.__CUSTOM_ASSET_MANAGER__ = CustomAssetManager
}
